import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  fetchReturnDetail,
  partialRefund,
  recordQc,
  reviewReturn,
  schedulePickup,
  shipBack,
  triggerRefund,
  updateInternalNotes,
  updatePickupAddress,
} from '@/api/returns';
import { blockCustomer } from '@/api/blocklist';
import { fetchPostcodeDetails } from '@/api/postcode';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Modal } from '@/components/ui/Modal';
import { SelectField } from '@/components/ui/SelectField';
import { TextField } from '@/components/ui/TextField';
import { TextArea } from '@/components/ui/TextArea';
import { StatusBadge, Badge } from '@/components/ui/Badge';
import { CheckIcon, XIcon, MailIcon, PhoneIcon, ShopifyIcon, FlagIcon, PencilIcon } from '@/components/icons';
import { ErrorState, LoadingState } from '@/components/feedback/StateViews';
import { useAsyncData } from '@/hooks/useAsyncData';
import { toast } from '@/store/useToastStore';
import { DEFAULT_DECLINE_REASONS, REFUND_MODE_OPTIONS, reasonLabel, refundModeLabel } from '@/constants/defaults';
import { formatCurrency, formatDate, formatDateTime, formatNumber, humanizeEnum, toNumber } from '@/lib/format';
import { cn } from '@/lib/cn';
import { errorMessageOf } from '@/lib/errors';
import type {
  PickupAddress,
  QcDisposition,
  QcOutcome,
  RefundMode,
  ReturnCustomer,
  ReturnDetailResponse,
  ReturnRecord,
} from '@/types';
import { paths } from '@/router/paths';

type ActiveModal =
  | 'approve'
  | 'reject'
  | 'pickup'
  | 'qc'
  | 'refund'
  | 'partial'
  | 'shipback'
  | 'block'
  | 'edit-address'
  | 'flagqc'
  | 'qcpass'
  | 'qcfail'
  | null;

const TERMINAL_STATUSES = ['REFUNDED', 'EXCHANGED', 'REJECTED', 'CANCELLED', 'CLOSED'];

/**
 * Net refund to the customer. Prefers the server-computed `netRefund` (it accounts for
 * exchange price differences); falls back to `baseRefund − fees + bonus` for older payloads.
 */
function computeNetRefund(record: ReturnRecord): number {
  if (record.netRefund != null) {
    return toNumber(record.netRefund);
  }
  return (
    toNumber(record.baseRefund ?? record.itemSubtotal) -
    toNumber(record.restockingFee) -
    toNumber(record.returnShippingFee) +
    toNumber(record.refundBonus)
  );
}

function exchangeItemsTotal(record: ReturnRecord): number {
  return (record.exchangeItems ?? []).reduce((sum, item) => sum + toNumber(item.unitPrice) * item.quantity, 0);
}

export function ReturnDetailScreen() {
  const navigate = useNavigate();
  const params = useParams();
  const returnId = params.returnId ?? '';

  const { data, errorMessage, isLoading, reload } = useAsyncData<ReturnDetailResponse | null>(
    (signal) => fetchReturnDetail(returnId, signal),
    [returnId],
  );

  const [activeModal, setActiveModal] = useState<ActiveModal>(null);
  const [isBusy, setIsBusy] = useState(false);
  const [isSavingNotes, setIsSavingNotes] = useState(false);

  async function runAction(action: () => Promise<unknown>, successMessage: string) {
    setIsBusy(true);
    try {
      await action();
      toast.success(successMessage);
      setActiveModal(null);
      reload();
    } catch (error) {
      toast.error(errorMessageOf(error));
    } finally {
      setIsBusy(false);
    }
  }

  async function handleSaveNotes(notes: string) {
    setIsSavingNotes(true);
    try {
      await updateInternalNotes(returnId, notes);
      toast.success('Note saved');
      reload();
    } catch (error) {
      toast.error(errorMessageOf(error));
    } finally {
      setIsSavingNotes(false);
    }
  }

  if (isLoading) {
    return <LoadingState message="Loading return…" />;
  }
  if (errorMessage || !data) {
    return <ErrorState message={errorMessage ?? 'Return not found.'} onRetry={reload} />;
  }

  const record = data.return;
  const events = data.events ?? [];
  const status = record.status;
  const isPendingReview = status === 'PENDING_REVIEW';
  const isApproved = status === 'APPROVED' || status === 'AUTO_APPROVED';
  const isAtWarehouse = status === 'RECEIVED_AT_WAREHOUSE';
  const isPendingQc = status === 'PENDING_QC';
  const isQcFailed = status === 'QC_FAILED' || status === 'QC_FAILED_PENDING_REVIEW';
  const isTerminal = TERMINAL_STATUSES.includes(status);
  const canRefund = !record.refundInitiatedAt &&!isTerminal && !isPendingReview && computeNetRefund(record) > 0;
  //pending qc, qc failed 

  return (
    <div className="animate-fade-in">
      <button
        type="button"
        onClick={() => navigate(paths.returns)}
        className="mb-4 text-sm font-medium text-brand hover:underline"
      >
        ← Back to returns
      </button>

      <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="font-mono text-2xl font-bold text-ink">{record.returnId}</h1>
            <StatusBadge status={status} />
            {record.isExchange ? <Badge tone="progress">Exchange</Badge> : null}
            {record.testMode ? <Badge tone="pending">Test</Badge> : null}
          </div>
          <p className="mt-1.5 text-sm text-ink-muted">
            Submitted <span className="font-medium text-ink">{formatDateTime(record.createdAt)}</span>
            {record.shopifyOrderNumber ? (
              <>
                {' · '}Order <span className="font-medium text-brand">{record.shopifyOrderNumber}</span>
              </>
            ) : null}
            {record.customerName ? <> · {record.customerName}</> : null}
          </p>
          {record.isExchange && record.exchangeShopifyOrderNumber ? (
            <p className="mt-1 text-sm text-ink-muted">
              Exchange order{' '}
              {record.exchangeShopifyOrderAdminLink ? (
                <a
                  href={record.exchangeShopifyOrderAdminLink}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 font-medium text-brand hover:underline"
                >
                  {record.exchangeShopifyOrderNumber}
                  <ShopifyIcon />
                </a>
              ) : (
                <span className="font-medium text-brand">{record.exchangeShopifyOrderNumber}</span>
              )}
            </p>
          ) : null}
        </div>
        <div className="flex flex-wrap gap-2">
          {isPendingReview ? (
            <>
             { false && <Button variant="secondary" onClick={() => setActiveModal('flagqc')} leadingIcon={<FlagIcon />}>
                Flag for QC
              </Button>}
              <Button variant="danger" onClick={() => setActiveModal('reject')} leadingIcon={<XIcon />}>
                Reject
              </Button>
              <Button onClick={() => setActiveModal('approve')} leadingIcon={<CheckIcon />}>
                Approve
              </Button>
            </>
          ) : null}
          {isPendingQc ? (
            <>
              <Button variant="danger" onClick={() => setActiveModal('qcfail')} leadingIcon={<XIcon />}>
                QC failed
              </Button>
              <Button onClick={() => setActiveModal('qcpass')} leadingIcon={<CheckIcon />}>
                QC passed
              </Button>
            </>
          ) : null}
          {isApproved ? (
            <Button onClick={() => setActiveModal('pickup')}>Schedule pickup</Button>
          ) : null}
          {isAtWarehouse || isQcFailed ? (
            <Button variant="secondary" onClick={() => setActiveModal('qc')}>
              Record QC
            </Button>
          ) : null}
          {canRefund ? (
            <Button variant="secondary" onClick={() => setActiveModal('refund')}>
              Process refund
            </Button>
          ) : null}
          {isQcFailed || isAtWarehouse ? (
            <>
              <Button variant="secondary" onClick={() => setActiveModal('partial')}>
                Partial refund
              </Button>
              <Button variant="ghost" onClick={() => setActiveModal('shipback')}>
                Ship back
              </Button>
            </>
          ) : null}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
        <div className="flex flex-col gap-4 lg:col-span-3">
          <OrderSummaryCard record={record} adminLink={data.shopifyOrderAdminLink} />
          <ItemsCard record={record} />
          <RefundBreakdownCard record={record} />
          <ShipmentCard record={record} />
        </div>

        <div className="flex flex-col gap-4 lg:col-span-2">
          <CustomerCard record={record} customer={data.customer} onBlock={() => setActiveModal('block')} />
          <AddressCard record={record} onEdit={() => setActiveModal('edit-address')} />
          <ActivityCard events={events} />
          <InternalNotesCard record={record} isBusy={isSavingNotes} onSave={handleSaveNotes} />
        </div>
      </div>

      <ApproveModal
        isOpen={activeModal === 'approve'}
        isBusy={isBusy}
        onClose={() => setActiveModal(null)}
        onConfirm={(notes) =>
          runAction(() => reviewReturn(returnId, 'APPROVED', notes), 'Return approved')
        }
      />
      <RejectModal
        isOpen={activeModal === 'reject'}
        isBusy={isBusy}
        onClose={() => setActiveModal(null)}
        onConfirm={(notes) => runAction(() => reviewReturn(returnId, 'REJECTED', notes), 'Return rejected')}
      />
      <ConfirmModal
        isOpen={activeModal === 'flagqc'}
        title="Flag for QC"
        message="This routes the return to the QC queue for inspection before any refund is issued."
        confirmLabel="Flag for QC"
        isBusy={isBusy}
        onClose={() => setActiveModal(null)}
        onConfirm={() => runAction(() => reviewReturn(returnId, 'PENDING_QC'), 'Return flagged for QC')}
      />
      <ConfirmModal
        isOpen={activeModal === 'qcpass'}
        title="Mark QC passed"
        message="Confirm the item passed inspection. The return can then proceed to refund/exchange."
        confirmLabel="QC passed"
        isBusy={isBusy}
        onClose={() => setActiveModal(null)}
        onConfirm={() => runAction(() => reviewReturn(returnId, 'QC_PASSED'), 'QC marked as passed')}
      />
      <ConfirmModal
        isOpen={activeModal === 'qcfail'}
        title="Mark QC failed"
        message="Confirm the item failed inspection. You can then issue a partial refund or ship it back."
        confirmLabel="QC failed"
        isBusy={isBusy}
        onClose={() => setActiveModal(null)}
        onConfirm={() => runAction(() => reviewReturn(returnId, 'QC_FAILED'), 'QC marked as failed')}
      />
      <ConfirmModal
        isOpen={activeModal === 'pickup'}
        title="Schedule pickup"
        message="This will create a reverse shipment and schedule a courier pickup via Shiprocket."
        confirmLabel="Schedule pickup"
        isBusy={isBusy}
        onClose={() => setActiveModal(null)}
        onConfirm={() => runAction(() => schedulePickup(returnId), 'Pickup scheduling initiated')}
      />
      <ConfirmModal
        isOpen={activeModal === 'refund'}
        title="Process refund"
        message={`Issue the refund of ${formatCurrency(computeNetRefund(record))} via ${refundModeLabel(record.refundMode)}?`}
        confirmLabel="Process refund"
        isBusy={isBusy}
        onClose={() => setActiveModal(null)}
        onConfirm={() => runAction(() => triggerRefund(returnId), 'Refund initiated')}
      />
      <QcModal
        isOpen={activeModal === 'qc'}
        isBusy={isBusy}
        onClose={() => setActiveModal(null)}
        onConfirm={(outcome, disposition, notes) =>
          runAction(() => recordQc(returnId, outcome, disposition, notes), `QC ${outcome.toLowerCase()} recorded`)
        }
      />
      <PartialRefundModal
        isOpen={activeModal === 'partial'}
        maxAmount={computeNetRefund(record)}
        isBusy={isBusy}
        onClose={() => setActiveModal(null)}
        onConfirm={(amount, reason, mode) =>
          runAction(() => partialRefund(returnId, amount, reason, mode), 'Partial refund initiated')
        }
      />
      <ShipBackModal
        isOpen={activeModal === 'shipback'}
        isBusy={isBusy}
        onClose={() => setActiveModal(null)}
        onConfirm={(note) => runAction(() => shipBack(returnId, note), 'Item marked to ship back')}
      />
      <PickupAddressModal
        isOpen={activeModal === 'edit-address'}
        address={record.pickupAddress}
        isBusy={isBusy}
        onClose={() => setActiveModal(null)}
        onConfirm={(address) =>
          runAction(() => updatePickupAddress(returnId, address), 'Pickup address updated')
        }
      />
      <BlockModal
        isOpen={activeModal === 'block'}
        defaultEmail={record.customerEmail ?? ''}
        defaultPhone={record.customerPhone ?? ''}
        defaultName={record.customerName ?? ''}
        isBusy={isBusy}
        onClose={() => setActiveModal(null)}
        onConfirm={(email, phone, reason, name) =>
          runAction(
            () =>
              blockCustomer({
                customerEmail: email,
                customerPhone: phone.trim() || undefined,
                reason,
                customerName: name,
              }),
            'Customer blocked',
          )
        }
      />
    </div>
  );
}

// ── Shared card chrome ──────────────────────────────────────────────────────
function SectionCard({
  title,
  action,
  children,
}: {
  title: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Card padded={false} className="overflow-hidden">
      <div className="flex items-center justify-between gap-3 border-b border-line px-3 py-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-subtle">{title}</h2>
        {action}
      </div>
      <div className="p-4 pt-2">{children}</div>
    </Card>
  );
}

// ── Left column cards ───────────────────────────────────────────────────────
function OrderSummaryCard({ record, adminLink }: { record: ReturnRecord; adminLink?: string | null }) {
  return (
    <SectionCard
      title="Order summary"
      action={
        adminLink ? (
          <a
            href={adminLink}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-brand hover:underline"
          >
            <ShopifyIcon /> View on Shopify
          </a>
        ) : null
      }
    >
      <dl className="grid grid-cols-2 gap-y-4 gap-x-3 sm:grid-cols-4">
        <Stat label="Order" value={record.shopifyOrderNumber ?? '—'} mono />
        <Stat label="Submitted" value={formatDate(record.createdAt) || '—'} />
        <Stat label="Reason" value={reasonLabel(record.reasonSlug)} />
        <Stat
          label="Order total"
          value={record.totalOrderValue != null ? formatCurrency(record.totalOrderValue) : '—'}
        />
      </dl>
    </SectionCard>
  );
}

function ItemsCard({ record }: { record: ReturnRecord }) {
  const items = record.items ?? [];
  if (items.length === 0) {
    return null;
  }
  return (
    <SectionCard
      title={`Items being returned · ${items.length}`}
      action={
        <span className="text-sm text-ink-muted">
          Refund mode: <span className="font-semibold text-ink">{record.isExchange ? 'Exchange' : refundModeLabel(record.refundMode)}</span>
        </span>
      }
    >
      <ul className="flex flex-col gap-5">
        {items.map((item, index) => (
          <li key={`${item.sku ?? item.productTitle}-${index}`} className="flex gap-4">
            {item.imageUrl ? (
              <img
                src={item.imageUrl}
                alt=""
                className="h-16 w-16 flex-shrink-0 rounded-lg border border-line object-cover"
              />
            ) : (
              <span className="h-16 w-16 flex-shrink-0 rounded-lg bg-gradient-to-br from-violet-400 to-violet-600" />
            )}
            <div className="min-w-0 flex-1">
              <div className="flex items-start justify-between gap-3">
                <p className="text-sm font-semibold text-ink">{item.productTitle}</p>
                <div className="flex-shrink-0 text-right">
                  <div className="text-sm font-semibold text-ink">{formatCurrency(item.unitPrice)}</div>
                  <div className="text-xs text-ink-subtle">per unit</div>
                </div>
              </div>
              <p className="mt-0.5 text-xs text-ink-subtle">
                {[item.variantTitle, item.sku ? `SKU ${item.sku}` : null, `Qty ${item.quantity}`]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
              {item.reasonSlug ? (
                <span className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-brand/10 px-2.5 py-1 text-xs font-medium text-brand">
                  <span className="h-1.5 w-1.5 rounded-full bg-brand" />
                  {reasonLabel(item.reasonSlug)}
                </span>
              ) : null}
              {item.reasonNote || record.reasonNote ? (
                <div className="mt-3 rounded-lg border-l-2 border-brand/40 bg-surface-sunken/60 px-3 py-2">
                  <p className="text-xs font-medium text-ink-subtle">Customer note</p>
                  <p className="mt-0.5 text-sm text-ink-muted">“{item.reasonNote ?? record.reasonNote}”</p>
                </div>
              ) : null}
              {item.photoUrls && item.photoUrls.length > 0 ? (
                <div className="mt-3">
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-subtle">
                    Uploaded images
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {item.photoUrls.map((photoUrl, photoIndex) => (
                      <a key={photoIndex} href={photoUrl} target="_blank" rel="noreferrer">
                        <img
                          src={photoUrl}
                          alt={`Item photo ${photoIndex + 1}`}
                          className="h-36 w-36 rounded-lg border border-line object-cover transition-opacity hover:opacity-90"
                        />
                      </a>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          </li>
        ))}
      </ul>

      {record.isExchange && record.exchangeItems && record.exchangeItems.length > 0 ? (
        <div className="mt-5 border-t border-line pt-5">
          <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-ink-subtle">
            Exchanging for · {record.exchangeItems.length}
          </p>
          <ul className="flex flex-col gap-4">
            {record.exchangeItems.map((item, index) => (
              <li key={`${item.sku ?? item.shopifyVariantId}-${index}`} className="flex items-start gap-4">
                {item.productImage ? (
                  <img
                    src={item.productImage}
                    alt=""
                    className="h-12 w-12 flex-shrink-0 rounded-lg border border-line object-cover"
                  />
                ) : null}
                <div className="flex min-w-0 flex-1 items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-ink">{item.productTitle || item.variantTitle || 'Replacement item'}</p>
                    <p className="mt-0.5 text-xs text-ink-subtle">
                      {[item.variantTitle !== item.productTitle ? item.variantTitle : null, item.sku ? `SKU ${item.sku}` : null, `Qty ${item.quantity}`]
                        .filter(Boolean)
                        .join(' · ')}
                    </p>
                  </div>
                  <div className="flex-shrink-0 text-right">
                    <div className="text-sm font-semibold text-ink">{formatCurrency(item.unitPrice)}</div>
                    <div className="text-xs text-ink-subtle">per unit</div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </SectionCard>
  );
}

function RefundBreakdownCard({ record }: { record: ReturnRecord }) {
  const restockingFee = toNumber(record.restockingFee);
  const shippingFee = toNumber(record.returnShippingFee);
  const bonus = toNumber(record.refundBonus);
  const netRefund = computeNetRefund(record);
  const isExchange = Boolean(record.isExchange);
  const exchangeValue = exchangeItemsTotal(record);
  const baseRefund = record.baseRefund != null ? toNumber(record.baseRefund) : toNumber(record.itemSubtotal);

  return (
    <SectionCard title="Refund breakdown">
      <dl className="flex flex-col">
        <BreakdownRow label="Item subtotal" value={formatCurrency(record.itemSubtotal)} />
        {isExchange && exchangeValue > 0 ? (
          <>
            <BreakdownRow label="Exchange items value" value={`− ${formatCurrency(exchangeValue)}`} />
            <BreakdownRow label="Price difference" value={formatCurrency(baseRefund)} />
          </>
        ) : null}
        <BreakdownRow
          label="Restocking fee"
         // sublabel={restockingFee === 0 ? 'Waived' : undefined}
          value={`− ${formatCurrency(restockingFee)}`}
        />
        <BreakdownRow
          label="Return shipping fee"
         // sublabel={shippingFee === 0 ? 'Merchant pays' : undefined}
          value={`− ${formatCurrency(shippingFee)}`}
        />
        {bonus > 0 ? (
          <BreakdownRow
            label="Bonus credit"
            sublabel={record.bonusPct ? `${record.bonusPct}% bonus` : undefined}
            value={`+ ${formatCurrency(bonus)}`}
          />
        ) : null}
        <div className="mt-2 flex items-center justify-between pt-3">
          <dt className="text-base font-bold text-ink">
            {isExchange ? (netRefund > 0 ? 'Net refund + exchange' : 'Exchange — no refund due') : 'Net refund to customer'}
          </dt>
          <dd className="text-lg font-bold text-ink">{formatCurrency(netRefund)}</dd>
        </div>
      </dl>
      <p className="mt-3 text-sm text-ink-muted">
        Issued as <span className="font-semibold text-ink">{isExchange ? `Exchange ${netRefund > 0 ? `with remaining refund: ${refundModeLabel(record.refundMode)}` : ''  }` : refundModeLabel(record.refundMode)}</span>
      </p>
    </SectionCard>
  );
}

function ShipmentCard({ record }: { record: ReturnRecord }) {
  if (!record.shiprocketAwb && !record.courierName && !record.trackingNumber && !record.shiprocketOrderId) {
    return null;
  }
  return (
    <SectionCard title="Shipment">
      <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3">
        <Stat label="Courier" value={record.courierName ?? '—'} />
        <Stat label="AWB" value={record.shiprocketAwb ?? record.shiprocketAwb ?? '—'} mono />
        <Stat label="Shiprocket order" value={record.shiprocketOrderId ?? '—'} mono />
      </dl>
    </SectionCard>
  );
}

// ── Right column cards ──────────────────────────────────────────────────────
function initials(name?: string): string {
  if (!name) {
    return '?';
  }
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || '?';
}

function CustomerCard({
  record,
  customer,
  onBlock,
}: {
  record: ReturnRecord;
  customer?: ReturnCustomer | null;
  onBlock: () => void;
}) {
  const name = customer?.name || record.customerName || '—';
  const email = customer?.email || record.customerEmail || '';
  const phone = customer?.phone || record.customerPhone || '';
  const hasStats =
    customer && (customer.totalOrders != null || customer.totalReturns != null || customer.totalSpent != null);
  return (
    <SectionCard title="Customer">
      <div className="flex items-center gap-3">
        <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-info-soft text-sm font-semibold text-info">
          {initials(name)}
        </span>
        <div className="min-w-0">
          <p className="truncate text-base font-semibold text-ink">{name}</p>
          {email ? <p className="truncate text-sm text-ink-muted">{email}</p> : null}
        </div>
      </div>

      {hasStats ? (
        <dl className="mt-4 grid grid-cols-3 gap-3">
          <div>
            <dt className="text-xs text-ink-subtle">Orders</dt>
            <dd className="mt-0.5 text-lg font-bold text-ink">{formatNumber(customer?.totalOrders)}</dd>
          </div>
          <div>
            <dt className="text-xs text-ink-subtle">Returns</dt>
            <dd className="mt-0.5 text-lg font-bold text-ink">{formatNumber(customer?.totalReturns)}</dd>
          </div>
          <div>
            <dt className="text-xs text-ink-subtle">Lifetime</dt>
            <dd className="mt-0.5 text-lg font-bold text-ink">{formatCurrency(customer?.totalSpent)}</dd>
          </div>
        </dl>
      ) : null}

      <div className="mt-4 flex flex-wrap items-center gap-2">
        {email ? (
          <a
            href={`mailto:${email}`}
            className="inline-flex min-w-0 flex-1 items-center justify-center gap-0.5 rounded-lg border border-line px-3 py-2 text-sm font-small text-ink transition-colors hover:bg-surface-sunken"
          >
            <MailIcon />
            <span className="truncate">{email}</span>
          </a>
        ) : null}
        {phone ? (
          <a
            href={`tel:${phone}`}
            className="inline-flex min-w-0 flex-1 items-center justify-center gap-0.5 rounded-lg border border-line px-3 py-2 text-sm font-small text-ink transition-colors hover:bg-surface-sunken"
          >
            <PhoneIcon />
            <span className="truncate">{phone}</span>
          </a>
        ) : null}
        <button
          type="button"
          onClick={onBlock}
          className="rounded-lg border border-line px-3 py-2 text-sm font-medium text-danger transition-colors hover:bg-danger-soft"
        >
          Block
        </button>
      </div>
    </SectionCard>
  );
}

function AddressCard({ record, onEdit }: { record: ReturnRecord; onEdit: () => void }) {
  const address = record.pickupAddress;
  const cityLine = [address?.city, address?.pincode].filter(Boolean).join(' — ');
  const regionLine = [address?.state, address?.country].filter(Boolean).join(', ');
  const hasAddress = Boolean(
    address && (address.name || address.line1 || address.city || address.phone),
  );
  return (
    <SectionCard
      title="Pickup address"
      action={
        <button
          type="button"
          onClick={onEdit}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-brand hover:underline"
        >
          <PencilIcon /> Edit
        </button>
      }
    >
      {hasAddress ? (
        <div className="text-sm text-ink-muted">
          {address?.name ? <p className="font-semibold text-ink">{address.name}</p> : null}
          {address?.line1 ? <p className="mt-1">{address.line1}</p> : null}
          {address?.line2 ? <p>{address.line2}</p> : null}
          {cityLine ? <p>{cityLine}</p> : null}
          {regionLine ? <p>{regionLine}</p> : null}
          {address?.phone ? <p className="mt-2">{address.phone}</p> : null}
        </div>
      ) : (
        <p className="text-sm text-ink-subtle">No pickup address yet.</p>
      )}
    </SectionCard>
  );
}

const ACTIVITY_TONE: Record<string, string> = {
  AUTOPILOT: 'bg-brand',
  CUSTOMER: 'bg-info',
  MERCHANT: 'bg-ink',
  SYSTEM: 'bg-ink-subtle',
};

function ActivityCard({ events }: { events: ReturnDetailResponse['events'] }) {
  return (
    <SectionCard title="Activity">
      {events.length === 0 ? (
        <p className="text-sm text-ink-muted">No events recorded.</p>
      ) : (
        <ol className="flex flex-col">
          {events.map((event, index) => {
            const isLast = index === events.length - 1;
            return (
              <li key={index} className="flex gap-3">
                <div className="flex flex-col items-center">
                  <span
                    className={cn('mt-1 h-2.5 w-2.5 flex-shrink-0 rounded-full', ACTIVITY_TONE[event.actorType] ?? 'bg-ink-subtle')}
                    aria-hidden
                  />
                  {!isLast ? <span className="w-px flex-1 bg-line" aria-hidden /> : null}
                </div>
                <div className={cn('min-w-0', isLast ? 'pb-0' : 'pb-5')}>
                  <p className="text-sm text-ink">
                    <span className="font-semibold">{humanizeEnum(event.actorType)}</span>{' '}
                    <span className="text-ink-muted">{event.note || humanizeEnum(event.eventType)}</span>
                  </p>
                  <p className="mt-0.5 text-xs text-ink-subtle">{formatDateTime(event.createdAt)}</p>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </SectionCard>
  );
}

function InternalNotesCard({
  record,
  isBusy,
  onSave,
}: {
  record: ReturnRecord;
  isBusy: boolean;
  onSave: (notes: string) => void;
}) {
  const [notes, setNotes] = useState('');

  // useEffect(() => {
  //   setNotes(record.internalNotes ?? '');
  // }, [record.internalNotes]);

  const hasChanges = notes.trim() !== (record.internalNotes ?? '').trim();

  return (
    <SectionCard title="Internal notes">
      <div className="flex flex-col gap-3">
        <TextArea
          placeholder="Leave a note for the team…"
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
        />
        <div className="flex justify-end">
          <Button size="sm" onClick={() => onSave(notes.trim())} isLoading={isBusy} disabled={!hasChanges}>
            Save note
          </Button>
        </div>
      </div>
    </SectionCard>
  );
}

function Stat({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-ink-subtle">{label}</dt>
      <dd className={cn('mt-1 text-sm font-semibold text-ink', mono && 'font-mono')}>{value}</dd>
    </div>
  );
}

function BreakdownRow({ label, sublabel, value }: { label: string; sublabel?: string; value: string }) {
  return (
    <div className="flex items-start justify-between border-b border-line py-3 last:border-b-0">
      <div>
        <dt className="text-sm text-ink">{label}</dt>
        {sublabel ? <dd className="text-xs text-ink-subtle">{sublabel}</dd> : null}
      </div>
      <dd className="text-sm font-medium text-ink">{value}</dd>
    </div>
  );
}

// ── Action modals ───────────────────────────────────────────────────────────
function modalFooter(onClose: () => void, onConfirm: () => void, confirmLabel: string, isBusy: boolean, danger?: boolean) {
  return (
    <>
      <Button variant="secondary" onClick={onClose} disabled={isBusy}>
        Cancel
      </Button>
      <Button variant={danger ? 'danger' : 'primary'} onClick={onConfirm} isLoading={isBusy}>
        {confirmLabel}
      </Button>
    </>
  );
}

function ConfirmModal({
  isOpen,
  title,
  message,
  confirmLabel,
  isBusy,
  onClose,
  onConfirm,
}: {
  isOpen: boolean;
  title: string;
  message: string;
  confirmLabel: string;
  isBusy: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} footer={modalFooter(onClose, onConfirm, confirmLabel, isBusy)}>
      <p className="text-sm text-ink-muted">{message}</p>
    </Modal>
  );
}

function ApproveModal({
  isOpen,
  isBusy,
  onClose,
  onConfirm,
}: {
  isOpen: boolean;
  isBusy: boolean;
  onClose: () => void;
  onConfirm: (notes: string) => void;
}) {
  const [notes, setNotes] = useState('');
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Approve return"
      footer={modalFooter(onClose, () => onConfirm(notes), 'Approve', isBusy)}
    >
      <p className="mb-3 text-sm text-ink-muted">Approving creates the reverse pickup and notifies the customer.</p>
      <TextArea label="Internal note (optional)" value={notes} onChange={(event) => setNotes(event.target.value)} />
    </Modal>
  );
}

function RejectModal({
  isOpen,
  isBusy,
  onClose,
  onConfirm,
}: {
  isOpen: boolean;
  isBusy: boolean;
  onClose: () => void;
  onConfirm: (notes: string) => void;
}) {
  const [reason, setReason] = useState(DEFAULT_DECLINE_REASONS[0]);
  const [message, setMessage] = useState('');
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Reject return"
      footer={modalFooter(onClose, () => onConfirm([reason, message].filter(Boolean).join(' — ')), 'Reject', isBusy, true)}
    >
      <div className="flex flex-col gap-3">
        <SelectField
          label="Decline reason"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          options={DEFAULT_DECLINE_REASONS.map((value) => ({ value, label: value }))}
        />
        <TextArea
          label="Message to customer (optional)"
          value={message}
          onChange={(event) => setMessage(event.target.value)}
        />
      </div>
    </Modal>
  );
}

function QcModal({
  isOpen,
  isBusy,
  onClose,
  onConfirm,
}: {
  isOpen: boolean;
  isBusy: boolean;
  onClose: () => void;
  onConfirm: (outcome: QcOutcome, disposition: QcDisposition, notes: string) => void;
}) {
  const [outcome, setOutcome] = useState<QcOutcome>('PASSED');
  const [disposition, setDisposition] = useState<QcDisposition>('RESTOCK');
  const [notes, setNotes] = useState('');
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Record QC outcome"
      footer={modalFooter(onClose, () => onConfirm(outcome, disposition, notes), 'Save QC', isBusy)}
    >
      <div className="flex flex-col gap-3">
        <SelectField
          label="Outcome"
          value={outcome}
          onChange={(event) => setOutcome(event.target.value as QcOutcome)}
          options={[
            { value: 'PASSED', label: 'Passed' },
            { value: 'FAILED', label: 'Failed' },
          ]}
        />
        <SelectField
          label="Disposition"
          value={disposition}
          onChange={(event) => setDisposition(event.target.value as QcDisposition)}
          options={[
            { value: 'RESTOCK', label: 'Restock' },
            { value: 'DAMAGED', label: 'Damaged' },
            { value: 'MISSING', label: 'Missing' },
            { value: 'FRAUDULENT', label: 'Fraudulent' },
            { value: 'DISPOSED', label: 'Disposed' },
          ]}
        />
        <TextArea label="Notes (optional)" value={notes} onChange={(event) => setNotes(event.target.value)} />
      </div>
    </Modal>
  );
}

function PartialRefundModal({
  isOpen,
  maxAmount,
  isBusy,
  onClose,
  onConfirm,
}: {
  isOpen: boolean;
  maxAmount: number;
  isBusy: boolean;
  onClose: () => void;
  onConfirm: (amount: number, reason: string, mode?: RefundMode) => void;
}) {
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [mode, setMode] = useState('');
  const parsedAmount = toNumber(amount);
  const isValid = parsedAmount > 0 && parsedAmount <= maxAmount && reason.trim().length > 0;
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Partial refund"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={isBusy}>
            Cancel
          </Button>
          <Button
            onClick={() => onConfirm(parsedAmount, reason, (mode || undefined) as RefundMode | undefined)}
            isLoading={isBusy}
            disabled={!isValid}
          >
            Issue partial refund
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <TextField
          label={`Amount (max ${formatCurrency(maxAmount)})`}
          type="number"
          inputMode="decimal"
          value={amount}
          onChange={(event) => setAmount(event.target.value)}
        />
        <SelectField
          label="Refund mode (optional override)"
          value={mode}
          onChange={(event) => setMode(event.target.value)}
          options={[
            { value: '', label: 'Use customer’s choice' },
            ...REFUND_MODE_OPTIONS.map((value) => ({ value, label: refundModeLabel(value) })),
          ]}
        />
        <TextArea label="Reason" value={reason} onChange={(event) => setReason(event.target.value)} />
      </div>
    </Modal>
  );
}

function ShipBackModal({
  isOpen,
  isBusy,
  onClose,
  onConfirm,
}: {
  isOpen: boolean;
  isBusy: boolean;
  onClose: () => void;
  onConfirm: (note: string) => void;
}) {
  const [note, setNote] = useState('');
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Ship item back to customer"
      footer={modalFooter(onClose, () => onConfirm(note), 'Mark ship-back', isBusy)}
    >
      <p className="mb-3 text-sm text-ink-muted">
        Rejects the return and records that the item is being shipped back. Arrange the physical shipment in Shiprocket.
      </p>
      <TextArea label="Note (optional)" value={note} onChange={(event) => setNote(event.target.value)} />
    </Modal>
  );
}

function BlockModal({
  isOpen,
  defaultEmail,
  defaultPhone,
  defaultName,
  isBusy,
  onClose,
  onConfirm,
}: {
  isOpen: boolean;
  defaultEmail: string;
  defaultPhone: string;
  defaultName: string;
  isBusy: boolean;
  onClose: () => void;
  onConfirm: (email: string, phone: string, reason: string, name: string) => void;
}) {
  const [email, setEmail] = useState(defaultEmail);
  const [phone, setPhone] = useState(defaultPhone);
  const [reason, setReason] = useState('');
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Block customer"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={isBusy}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => onConfirm(email, phone, reason, defaultName)}
            isLoading={isBusy}
            disabled={!email.trim() || !reason.trim()}
          >
            Block customer
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <TextField label="Customer email" type="email" value={email} onChange={(event) => setEmail(event.target.value)} />
        <TextField
          label="Mobile number (optional)"
          type="tel"
          placeholder="+91 98765 43210"
          value={phone}
          onChange={(event) => setPhone(event.target.value)}
        />
        <TextArea label="Reason" value={reason} onChange={(event) => setReason(event.target.value)} />
      </div>
    </Modal>
  );
}

function PickupAddressModal({
  isOpen,
  address,
  isBusy,
  onClose,
  onConfirm,
}: {
  isOpen: boolean;
  address?: PickupAddress | null;
  isBusy: boolean;
  onClose: () => void;
  onConfirm: (address: PickupAddress) => void;
}) {
  const [form, setForm] = useState<PickupAddress>({});
  const [pincodeStatus, setPincodeStatus] = useState<'idle' | 'loading' | 'error'>('idle');

  // Reseed the form from the record whenever the modal is (re)opened.
  useEffect(() => {
    if (isOpen) {
      setForm({
        name: address?.name ?? '',
        phone: address?.phone ?? '',
        line1: address?.line1 ?? '',
        line2: address?.line2 ?? '',
        city: address?.city ?? '',
        state: address?.state ?? '',
        pincode: address?.pincode ?? '',
        country: address?.country ?? 'India',
      });
      setPincodeStatus('idle');
    }
  }, [isOpen, address]);

  function setField(key: keyof PickupAddress, value: string) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handlePincodeChange(rawValue: string) {
    const digits = rawValue.replace(/\D/g, '').slice(0, 6);
    if (digits.length < 6) {
      setForm((prev) => ({ ...prev, pincode: digits, city: '', state: '' }));
      setPincodeStatus('idle');
      return;
    }
    setForm((prev) => ({ ...prev, pincode: digits }));
    setPincodeStatus('loading');
    try {
      const details = await fetchPostcodeDetails(digits);
      setForm((prev) => ({ ...prev, city: details.city, state: details.state }));
      setPincodeStatus('idle');
    } catch (error) {
      setForm((prev) => ({ ...prev, city: '', state: '' }));
      setPincodeStatus('error');
      toast.error(errorMessageOf(error));
    }
  }

  const isValid =
    (form.name ?? '').trim().length > 0 &&
    (form.phone ?? '').trim().length > 0 &&
    (form.line1 ?? '').trim().length > 0 &&
    (form.city ?? '').trim().length > 0 &&
    (form.pincode ?? '').trim().length > 0 &&
    pincodeStatus !== 'loading';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Edit pickup address"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={isBusy}>
            Cancel
          </Button>
          <Button onClick={() => onConfirm(form)} isLoading={isBusy} disabled={!isValid}>
            Save address
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <TextField label="Name" value={form.name ?? ''} onChange={(event) => setField('name', event.target.value)} />
          <TextField
            label="Phone"
            value={form.phone ?? ''}
            onChange={(event) => setField('phone', event.target.value)}
          />
        </div>
        <TextField
          label="Address line 1"
          value={form.line1 ?? ''}
          onChange={(event) => setField('line1', event.target.value)}
        />
        <TextField
          label="Address line 2 (optional)"
          value={form.line2 ?? ''}
          onChange={(event) => setField('line2', event.target.value)}
        />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <TextField
            label="Pincode"
            value={form.pincode ?? ''}
            maxLength={6}
            inputMode="numeric"
            hint={pincodeStatus === 'loading' ? 'Looking up city & state…' : undefined}
            errorText={pincodeStatus === 'error' ? 'Could not find that pincode' : undefined}
            onChange={(event) => handlePincodeChange(event.target.value)}
          />
          <TextField
            label="Country"
            value={form.country ?? ''}
            disabled
            onChange={(event) => setField('country', event.target.value)}
          />
          <TextField
            label="City"
            value={form.city ?? ''}
            disabled
            className="disabled:cursor-not-allowed disabled:bg-surface-muted disabled:text-ink-subtle"
          />
          <TextField
            label="State"
            value={form.state ?? ''}
            disabled
            className="disabled:cursor-not-allowed disabled:bg-surface-muted disabled:text-ink-subtle"
          />
        </div>
      </div>
    </Modal>
  );
}
